import { Badge, Button, Group, Loader, Stack, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityRO } from '@routellers/types/dist/city/city.ro';
import { CountriesEnum } from '@routellers/types/dist/shared/enum/countries.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { api, apiErrorMessage } from '../api';
import { CityPipelineBar } from '../components/city-pipeline-bar';
import { ALL_COUNTRIES, CountrySelect } from '../components/country-select';
import { EntityThumb } from '../components/entity-thumb';
import { ListPagination } from '../components/list-pagination';
import { PageHeader } from '../components/page-header';
import { CityCreateDialog } from '../editors/city-create-dialog';
import { countryFlag } from '../utils/country-flag';
import { LIST_PAGE_SIZE, totalFromMeta } from '../utils/list';
import { localizedCityTitle } from '../utils/localized-city-title';
import { ROADMAP_CITIES } from '../utils/roadmap-cities';

export const CitiesPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [rows, setRows] = useState<CityRO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchApplied, setSearchApplied] = useState('');
    const [country, setCountry] = useState(ALL_COUNTRIES);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);

        try {
            const where: Record<string, unknown> = { readyForRoutell: null };
            if (country !== ALL_COUNTRIES) where.country = country;

            const { data } = await api.cities.query({
                page,
                limit: LIST_PAGE_SIZE,
                searchText: searchApplied.trim() || undefined,
                sort: 'id',
                order: 'ASC',
                // API `@IsNotEmpty()` rejects `{}` / missing where; null field keeps it valid.
                where: where as unknown as Record<string, never>,
            });

            setRows(data?.items || []);
            setTotal(totalFromMeta(data?.meta, data?.items?.length || 0));
        } catch (error) {
            notifications.show({
                color: 'orange',
                message: `${apiErrorMessage(error)} ${t('pages.cities.fallbackSuffix')}`,
            });
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setPage(1);
            setSearchApplied(search);
        }, 300);

        return () => window.clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        setPage(1);
    }, [country]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchApplied, country]);

    const roadmapIds = useMemo(() => new Set(ROADMAP_CITIES.map((c) => c.id)), []);

    const displayRows = useMemo(() => {
        if (rows.length) return rows;

        const q = searchApplied.trim().toLowerCase();

        return ROADMAP_CITIES.filter((c) => {
            if (country !== ALL_COUNTRIES && c.country !== country) return false;
            if (!q) return true;

            return c.title.toLowerCase().includes(q) || String(c.id).includes(q);
        })
            .slice(0, LIST_PAGE_SIZE)
            .map(
                (c) =>
                    ({
                        id: c.id,
                        title: c.title,
                        country: c.country as CountriesEnum,
                        questroomsCount: 0,
                        cityQuestsCount: 0,
                        readyForRoutell: false,
                    }) as unknown as CityRO,
            );
    }, [rows, searchApplied, country]);

    const usingFallback = !rows.length && !!displayRows.length;

    return (
        <Stack gap='md' style={{ flex: 1, minHeight: 0 }}>
            <PageHeader
                title={t('pages.cities.title')}
                action={
                    <Button onClick={() => setCreating(true)}>{t('pages.cities.create')}</Button>
                }
            />

            <Group gap='sm' wrap='wrap' align='center'>
                <TextInput
                    placeholder={t('pages.cities.searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    w={240}
                />
                <CountrySelect value={country} onChange={setCountry} />
                <CityPipelineBar onFinished={load} />
            </Group>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <Table.ScrollContainer minWidth={800}>
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>{t('pages.cities.columns.id')}</Table.Th>
                                    <Table.Th>{t('pages.cities.columns.city')}</Table.Th>
                                    <Table.Th>{t('pages.cities.columns.country')}</Table.Th>
                                    <Table.Th>{t('pages.cities.columns.routellers')}</Table.Th>
                                    <Table.Th>{t('pages.cities.columns.roadmap')}</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {displayRows.map((row) => (
                                    <Table.Tr
                                        key={row.id}
                                        style={{ cursor: usingFallback ? 'default' : 'pointer' }}
                                        onClick={() => {
                                            if (!usingFallback) navigate(`/cities/${row.id}`);
                                        }}
                                    >
                                        <Table.Td>{row.id}</Table.Td>
                                        <Table.Td>
                                            <EntityThumb
                                                src={row.photo}
                                                name={
                                                    localizedCityTitle(row, i18n.language) ||
                                                    `#${row.id}`
                                                }
                                                radius='md'
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            {row.country ? (
                                                <Group gap={8} wrap='nowrap'>
                                                    <Text span style={{ fontSize: 16, lineHeight: 1 }}>
                                                        {countryFlag(row.country)}
                                                    </Text>
                                                    <Text size='sm'>
                                                        {t(`enums.countries.${row.country}`, {
                                                            defaultValue: String(
                                                                row.country,
                                                            ).toUpperCase(),
                                                        })}
                                                    </Text>
                                                </Group>
                                            ) : (
                                                '—'
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            <Group gap={6}>
                                                <Badge
                                                    color={row.readyForRoutell ? 'green' : 'gray'}
                                                    variant='light'
                                                >
                                                    {row.readyForRoutell
                                                        ? t('pages.cities.ready')
                                                        : t('pages.cities.hidden')}
                                                </Badge>
                                                {row.earlyAccess !== false ? (
                                                    <Badge color='yellow' variant='light'>
                                                        {t('pages.cities.earlyAccess')}
                                                    </Badge>
                                                ) : null}
                                            </Group>
                                        </Table.Td>
                                        <Table.Td>
                                            {roadmapIds.has(row.id) ? (
                                                <Badge color='blue' variant='light'>
                                                    {t('pages.cities.priority')}
                                                </Badge>
                                            ) : (
                                                <Text size='sm' c='dimmed'>
                                                    —
                                                </Text>
                                            )}
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                                {!displayRows.length && (
                                    <Table.Tr>
                                        <Table.Td colSpan={5}>
                                            <Text c='dimmed' ta='center' py='md'>
                                                {t('pages.cities.empty')}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                )}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    {!usingFallback && (
                        <ListPagination page={page} totalItems={total} onChange={setPage} />
                    )}
                </>
            )}

            <CityCreateDialog
                opened={creating}
                onClose={() => setCreating(false)}
                onCreated={(id) => {
                    setCreating(false);
                    navigate(`/cities/${id}`);
                }}
            />
        </Stack>
    );
};
